// src/features/clinical/utils/clinicalHelpers.js
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const calculateBMI = (height, weight) => {
  if (!height || !weight) return null;
  const heightInM = height / 100;
  return (weight / (heightInM * heightInM)).toFixed(1);
};

export const getBMICategory = (bmi) => {
  if (!bmi) return null;
  const bmiValue = parseFloat(bmi);
  if (bmiValue < 18.5) return 'Underweight';
  if (bmiValue < 25) return 'Normal';
  if (bmiValue < 30) return 'Overweight';
  return 'Obese';
};

export const calculateAge = (dateOfBirth) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const generatePatientId = () => {
  const prefix = 'PT';
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}${random}`;
};

export const generateVisitId = () => {
  const prefix = 'VIS';
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `${prefix}-${year}-${random}`;
};

export const generatePrescriptionId = () => {
  const prefix = 'RX';
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `${prefix}-${year}-${random}`;
};

export const getVitalStatus = (vitalName, value) => {
  const ranges = {
    temperature: { normal: [36.1, 37.2], unit: '°C' },
    heartRate: { normal: [60, 100], unit: 'bpm' },
    respiratoryRate: { normal: [12, 20], unit: 'breaths/min' },
    bloodPressureSystolic: { normal: [90, 120], unit: 'mmHg' },
    bloodPressureDiastolic: { normal: [60, 80], unit: 'mmHg' },
    oxygenSaturation: { normal: [95, 100], unit: '%' }
  };

  const range = ranges[vitalName];
  if (!range) return null;

  if (value < range.normal[0]) return 'Low';
  if (value > range.normal[1]) return 'High';
  return 'Normal';
};

export const formatFrequency = (frequency) => {
  const frequencyMap = {
    'Once daily': 'OD',
    'Twice daily': 'BD',
    'Three times daily': 'TDS',
    'Four times daily': 'QID',
    'Every 4 hours': 'Q4H',
    'Every 6 hours': 'Q6H',
    'Every 8 hours': 'Q8H',
    'At bedtime': 'HS',
    'As needed': 'PRN'
  };
  return frequencyMap[frequency] || frequency;
};

export const calculateMedicineQuantity = (frequency, duration, dosagePerTime) => {
  const frequencyMap = {
    'Once daily': 1,
    'Twice daily': 2,
    'Three times daily': 3,
    'Four times daily': 4,
    'Every 4 hours': 6,
    'Every 6 hours': 4,
    'Every 8 hours': 3,
    'At bedtime': 1,
    'As needed': 0
  };
  
  const timesPerDay = frequencyMap[frequency] || 1;
  const days = parseInt(duration) || 1;
  return timesPerDay * days * (parseInt(dosagePerTime) || 1);
};

export const groupPrescriptionsByDate = (prescriptions) => {
  return prescriptions.reduce((groups, prescription) => {
    const date = new Date(prescription.prescriptionDate).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(prescription);
    return groups;
  }, {});
};

export const exportToCSV = (data, filename) => {
  const csvContent = data.map(row => Object.values(row).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};